import type { StickerComponent } from '../stickers/types'
import type { BaseAdjustmentComponent } from './BaseAdjustment'
import { formatYearsDelta } from './formatters'
import type { AgeAdjustmentSlot } from './useAdjustments'
import useHoldAcceleration, { AGE_TIERS } from './useHoldAcceleration'

export type AgeAdjustmentProps = {
  BaseAdjustment: BaseAdjustmentComponent
  slot: AgeAdjustmentSlot
  Sticker: StickerComponent
  label: string
  onInteractionEnd?: () => void
}

const noop = (): void => {}

/**
 * A `BaseAdjustment` specialized for integer-year ages. Each tick — including
 * the synchronous tap-tick fired by `start()` — calls `slot.step`, so the
 * slot mutates immediately and chart consumers re-render in lockstep with
 * the displayed value. `onPress` is a no-op to avoid a double-fire with the
 * press-start tick.
 *
 * Platform-agnostic — pass the platform's `BaseAdjustment`.
 */
export const AgeAdjustment = ({
  BaseAdjustment,
  slot,
  Sticker,
  label,
  onInteractionEnd,
}: AgeAdjustmentProps) => {
  const inc = useHoldAcceleration({
    tiers: AGE_TIERS,
    onTick: step => slot.step(step),
  })
  const dec = useHoldAcceleration({
    tiers: AGE_TIERS,
    onTick: step => slot.step(-step),
  })

  return (
    <BaseAdjustment
      Sticker={Sticker}
      label={label}
      value={slot.current}
      baseline={slot.baseline}
      formatValue={n => String(n)}
      formatDelta={formatYearsDelta}
      betterWhen={slot.betterWhen}
      onReset={slot.isModified ? slot.reset : undefined}
      onIncrement={noop}
      onDecrement={noop}
      onIncrementPressStart={inc.start}
      onIncrementPressEnd={() => {
        inc.stop()
        onInteractionEnd?.()
      }}
      onDecrementPressStart={dec.start}
      onDecrementPressEnd={() => {
        dec.stop()
        onInteractionEnd?.()
      }}
      onStep={dir => slot.step(dir)}
      onInteractionEnd={onInteractionEnd}
      isIncrementDisabled={slot.current >= slot.max}
      isDecrementDisabled={
        slot.current <= slot.min &&
        (slot.baseline >= slot.min || !slot.isModified)
      }
      displayStep={1}
      displayMin={slot.min}
      displayMax={slot.max}
    />
  )
}
